import { useState, type ReactNode } from 'react'
import {
  MATCH_VERDICT_LABELS,
  type AgentMatch,
  type AgentRunDetail,
  type MatchComparison,
  type MatchSide,
  type MatchSideComparison,
} from '~/entities/document-agent'
import { cn } from '~/shared/lib/cn'
import { pluralize } from '~/shared/lib/pluralize'
import { MATCH_STATUS_NOTES } from '../config'
import {
  agentMatchAgreement,
  describeFinalCaseSource,
  describeProductionOutcome,
  formatMethod,
  orderSides,
  productionMatchAgreement,
  type Agreement,
  type RunReview,
} from '../lib'
import { LongText } from './long-text'
import { MatchCandidatesTable } from './match-candidates-table'
import { MessageList } from './message-list'
import {
  AgreementBadge,
  AgreementDot,
  Hint,
  MaterialIcon,
  Mono,
  Muted,
  StorySection,
} from './primitives'

const SIDE_NAMES: Record<MatchSide, string> = { claim: 'Claim', pa: 'Prior authorization' }

function Ids({ ids }: { ids: string[] }) {
  return ids.length ? <Mono className="text-[13px] leading-5">{ids.join(' / ')}</Mono> : null
}

function PickRow({
  who,
  help,
  agreement,
  children,
  detail,
}: {
  who: string
  help: string
  agreement?: Agreement
  children: ReactNode
  detail?: ReactNode
}) {
  return (
    <div className="border-divider grid grid-cols-[220px_minmax(0,1fr)] gap-x-4 border-b py-2.5 last:border-b-0">
      <span className="text-text-secondary flex items-center gap-1.5 text-[13px] leading-5">
        {agreement ? <AgreementDot agreement={agreement} /> : <span className="size-2" />}
        {who}
        <Hint>{help}</Hint>
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-text-primary text-[13px] leading-5 wrap-anywhere">{children}</span>
        {detail && <Muted>{detail}</Muted>}
      </div>
    </div>
  )
}

function SidePicks({
  side,
  match,
  data,
  isScored,
}: {
  side: MatchSide
  match: AgentMatch
  data: MatchSideComparison | undefined
  isScored: boolean
}) {
  const pick = match[side]?.pick
  const pickIds = data?.row_marks.flat().find((mark) => mark.agent)?.ids ?? []
  const reviewer = data?.reviewer
  const today = data?.today
  const rules = data?.agent_rules
  const outcome = reviewer?.outcome
  const todayAgreement = isScored ? productionMatchAgreement(outcome) : undefined

  return (
    <div className="flex flex-col px-4">
      <PickRow
        who="Final case"
        help="The case the reviewer (or an auto-review rule) settled on. The agent's pick is scored against it."
        detail={
          isScored && (reviewer?.source || reviewer?.note) ? (
            <span className="flex flex-col">
              {reviewer.source && <span>{describeFinalCaseSource(reviewer.source)}</span>}
              {reviewer.note && (
                <span className="italic">{`Note on the record: “${reviewer.note}”`}</span>
              )}
            </span>
          ) : null
        }
      >
        {!isScored ? (
          <Muted>{"Not the document's side, so not scored"}</Muted>
        ) : !reviewer || reviewer.kind === 'pending' ? (
          <Muted>Not reviewed yet</Muted>
        ) : reviewer.kind === 'none' ? (
          'No matching case'
        ) : reviewer.kind === 'unresolved' ? (
          <Muted>Could not be resolved from the stored records</Muted>
        ) : (
          <Ids ids={reviewer.ids} />
        )}
      </PickRow>
      <PickRow
        who="Agent's pick"
        help="The case the agent chose from the candidates its lookups returned: match (one case), tie (several it could not choose between) or none."
        agreement={isScored ? agentMatchAgreement(data?.verdict) : undefined}
        detail={pick?.tied?.length ? `Tied between ${pick.tied.join(', ')}` : null}
      >
        {!pick ? (
          <Muted>No decision</Muted>
        ) : (
          <span className="inline-flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium capitalize">{pick.decision}</span>
            {pick.candidate && <Mono>{pick.candidate}</Mono>}
            {pickIds.length > 0 && (
              <>
                <MaterialIcon
                  name="arrow_forward"
                  className="text-text-tertiary self-center text-[14px]"
                />
                <Ids ids={pickIds} />
              </>
            )}
            {pick.confidence && <Muted>{`${pick.confidence} confidence`}</Muted>}
          </span>
        )}
      </PickRow>
      <PickRow
        who="Production on arrival"
        help="What today's matching rules picked when the document first arrived, from the candidates production looked up then."
        agreement={todayAgreement}
        detail={
          today
            ? `${today.rows} ${pluralize(today.rows, 'candidate')} · ${today.lookups} ${pluralize(today.lookups, 'lookup')}${isScored && outcome ? ` · ${describeProductionOutcome(outcome)}` : ''}`
            : null
        }
      >
        {!today ? (
          <Muted>No lookup on this side</Muted>
        ) : today.fired ? (
          <span className="inline-flex flex-wrap items-baseline gap-x-2">
            <span>{`${formatMethod(today.method)} rule`}</span>
            <MaterialIcon
              name="arrow_forward"
              className="text-text-tertiary self-center text-[14px]"
            />
            <Ids ids={today.ids} />
          </span>
        ) : (
          'Abstained'
        )}
      </PickRow>
      {rules && (
        <PickRow
          who="Production's rules on the agent's candidates"
          help="Today's matching rules re-run on the candidates the agent's lookups returned. Shows whether the agent's lookups alone would have let the rules decide."
        >
          {rules.fired ? (
            <span className="inline-flex flex-wrap items-baseline gap-x-2">
              <span>{`${formatMethod(rules.method)} rule`}</span>
              <MaterialIcon
                name="arrow_forward"
                className="text-text-tertiary self-center text-[14px]"
              />
              <Ids ids={rules.ids} />
              {rules.auto_review && <Muted>would auto-review</Muted>}
            </span>
          ) : (
            'Abstained'
          )}
        </PickRow>
      )}
    </div>
  )
}

function SideCard({
  side,
  match,
  data,
  isScored,
  defaultOpen,
}: {
  side: MatchSide
  match: AgentMatch
  data: MatchSideComparison | undefined
  isScored: boolean
  defaultOpen: boolean
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const sideData = match[side]
  const calls = sideData?.calls ?? []
  const pick = sideData?.pick
  const rowCount = calls.reduce((sum, call) => sum + (call.lookup?.rows?.length ?? 0), 0)
  const verdict = data?.verdict
  const callErrors = calls.flatMap((call) => call.errors ?? [])
  const callWarnings = calls.flatMap((call) => call.warnings ?? [])

  return (
    <div className="border-divider bg-surface-lowest rounded-xl border">
      <button
        type="button"
        onClick={() => setIsOpen((value) => !value)}
        className="flex w-full cursor-pointer flex-wrap items-center gap-x-3 gap-y-1 border-none bg-transparent px-4 py-3 text-left"
        aria-expanded={isOpen}
      >
        <MaterialIcon
          name={isOpen ? 'expand_less' : 'expand_more'}
          className="text-text-tertiary text-[20px]"
        />
        <span className="text-text-primary text-[15px] leading-6 font-semibold">
          {SIDE_NAMES[side]}
        </span>
        {isScored ? (
          <span className="bg-surface-medium text-text-primary rounded-full px-2 py-0.5 text-[11px] leading-4 font-medium">
            Scored
          </span>
        ) : (
          <Muted>Not scored</Muted>
        )}
        <span className="flex-1" />
        <Muted>
          {[
            `${calls.length} ${pluralize(calls.length, 'lookup')}`,
            `${rowCount} ${pluralize(rowCount, 'candidate')}`,
            sideData?.outcome && sideData.outcome !== 'looked_up'
              ? sideData.outcome.replace(/_/g, ' ')
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Muted>
        {isScored && verdict && (
          <AgreementBadge
            agreement={agentMatchAgreement(verdict)}
            label={MATCH_VERDICT_LABELS[verdict]?.label ?? verdict}
            tip={MATCH_VERDICT_LABELS[verdict]?.description}
          />
        )}
      </button>
      {isOpen && (
        <div className="border-divider flex flex-col gap-4 border-t pt-1 pb-4">
          <SidePicks
            side={side}
            match={match}
            data={data}
            isScored={isScored}
          />
          {pick?.reasoning && (
            <div className="bg-surface-low mx-4 flex gap-3 rounded-lg p-4">
              <MaterialIcon
                name="auto_awesome"
                className="text-primary-main mt-0.5 text-[18px]"
              />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="text-text-primary text-[13px] leading-5 font-semibold">
                  {"The agent's reasoning"}
                </span>
                <LongText text={pick.reasoning} />
              </div>
            </div>
          )}
          {(callErrors.length > 0 || callWarnings.length > 0) && (
            <div className="flex flex-col gap-2 px-4">
              <MessageList
                title="Lookups rejected"
                items={callErrors}
                isError
              />
              <MessageList
                title="Lookup warnings"
                items={callWarnings}
              />
            </div>
          )}
          <div className="flex flex-col gap-1">
            <span className="text-text-primary px-4 text-[13px] leading-5 font-semibold">
              {`Candidates the agent's lookups returned`}
            </span>
            <MatchCandidatesTable
              side={side}
              calls={calls}
              marks={data?.row_marks ?? []}
              hasFinal={
                isScored && (data?.reviewer?.kind === 'case' || data?.reviewer?.kind === 'none')
              }
            />
          </div>
        </div>
      )}
    </div>
  )
}

export function PatientCaseSection({
  run,
  review,
  comparison,
}: {
  run: AgentRunDetail
  review: RunReview
  comparison: MatchComparison | null
}) {
  const match = run.match
  if (!match) return null
  const matching = review.match
  const finalSide = matching?.side ?? null
  const sides = orderSides(finalSide).filter((side) => match[side])
  const session = match.session
  const note =
    match.status === 'deferred' && match.delay_s
      ? `${MATCH_STATUS_NOTES.deferred} Delayed ${match.delay_s} s at the last attempt.`
      : MATCH_STATUS_NOTES[match.status]

  return (
    <StorySection
      id="patient-case"
      title="Patient case"
      subtitle="Which patient case the document belongs to. The agent looked up candidates and picked one; production's rules picked on arrival; the final case is what the reviewer settled on."
      bare
    >
      <div className="flex flex-col gap-3">
        {!!(
          note ||
          session?.error ||
          matching?.excluded ||
          match.transcript_error ||
          match.pick_errors?.length
        ) && (
          <div className="border-divider bg-surface-lowest flex flex-col gap-2 rounded-xl border px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-text-secondary text-[13px] leading-5">Matching session</span>
              <span
                className={cn('rounded-full px-2 py-0.5 text-[12px] leading-4 font-medium', {
                  'bg-alert-success-fill text-success-content': match.status === 'completed',
                  'bg-alert-error-fill text-alert-error-content':
                    match.status.startsWith('error') || match.status.startsWith('incomplete'),
                  'bg-surface-medium text-text-primary':
                    match.status !== 'completed' &&
                    !match.status.startsWith('error') &&
                    !match.status.startsWith('incomplete'),
                })}
              >
                {match.status.replace(/_/g, ' ')}
              </span>
            </div>
            {note && <Muted className="text-[13px] leading-5">{note}</Muted>}
            {session?.error && (
              <span className="text-alert-error-content text-[13px] leading-5">{`The session ended with ${session.status}: ${session.error}`}</span>
            )}
            {matching?.excluded && (
              <Muted className="text-[13px] leading-5">{`Not scored: the document is excluded (${matching.excluded}).`}</Muted>
            )}
            <MessageList
              title="Patient match submissions rejected"
              items={match.pick_errors ?? []}
              isError
            />
            {match.transcript_error && (
              <span className="text-alert-error-content text-[13px] leading-5">{`The matching transcript was not stored (${match.transcript_error}).`}</span>
            )}
          </div>
        )}
        {sides.map((side) => (
          <SideCard
            key={side}
            side={side}
            match={match}
            data={comparison?.sides[side]}
            isScored={side === finalSide}
            defaultOpen={side === finalSide || !finalSide}
          />
        ))}
      </div>
    </StorySection>
  )
}
